<template>
  <div class="analytics-chart">
    <analytics-area-chart
      v-if="type === 'area'"
      :chart-data="getChartData"
      :options="getChartOptions"
      :style="chartStyle">
    </analytics-area-chart>

    <analytics-bar-chart
      v-if="type === 'bar'"
      :chart-data="getChartData"
      :options="getChartOptions"
      :style="chartStyle">
    </analytics-bar-chart>

    <analytics-doughnut-chart
      v-if="type === 'doughnut'"
      :chart-data="getChartData"
      :options="getChartOptions"
      :style="chartStyle">
    </analytics-doughnut-chart>

    <analytics-pie-chart
      v-if="type === 'pie'"
      :chart-data="getChartData"
      :options="getChartOptions"
      :style="chartStyle">
    </analytics-pie-chart>

    <analytics-polar-area-chart
      v-if="type === 'polar-area'"
      :chart-data="getChartData"
      :options="getChartOptions"
      :style="chartStyle">
    </analytics-polar-area-chart>
  </div>
</template>

<script>
import colorMixin from '@/mixins/colors';

import AnalyticsAreaChart from './AnalyticsAreaChart';
import AnalyticsBarChart from './AnalyticsBarChart';
import AnalyticsDoughnutChart from './AnalyticsDoughnutChart';
import AnalyticsPieChart from './AnalyticsPieChart';
import AnalyticsPolarAreaChart from './AnalyticsPolarAreaChart';

export default {
  name: 'AnalyticsChart',
  mixins: [colorMixin],
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: 'bar',
    },
    chartStyle: {
      type: String,
      default: '',
    },
    showTooltips: {
      type: Boolean,
      default: true,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    legendPosition: {
      type: String,
      default: 'top',
    },
    scalesEnabled: {
      type: Boolean,
      default: true,
    },
    barPercentage: {
      type: Number,
      default: 1,
    },
    categoryPercentage: {
      type: Number,
      default: 0.7,
    },
  },
  components: {
    AnalyticsAreaChart,
    AnalyticsBarChart,
    AnalyticsDoughnutChart,
    AnalyticsPieChart,
    AnalyticsPolarAreaChart,
  },
  computed: {
    getChartData() {
      let chartDatasets = [
        {
          backgroundColor: this.data.map(s => this.generateTitleColor(s[0])),
          data: this.data.map(s => s[1]),
        },
      ];

      // Determine if we're using multiple datasets
      // allows us to send in data as:
      // [
      //   ['SABC', {'revenue': 12233, 'count': 1222}],
      //   ['FirstRand Bank Ltd', {'revenue': 45433, 'count': 4552}]
      // ]
      if (this.data.length && this.data[0][1] instanceof Object) {
        // Get our data split into different sets based on their keys
        const sets = {};
        this.data.forEach(data => {
          Object.keys(data[1]).forEach(key => {
            if (!(key in sets)) {
              sets[key] = [];
            }
            sets[key].push(data[1][key]);
          });
        });

        // Now build the actual chart data
        chartDatasets = Object.keys(sets).map(key => ({
          label: key,
          backgroundColor: this.generateRGBAColor(key, 0.5),
          borderColor: this.generateRGBAColor(key, 1),
          pointBackgroundColor: this.generateRGBAColor(key, 1),
          pointBorderColor: '#fff',
          data: sets[key],
        }));
      }

      const chartData = {
        labels: this.data.map(s => s[0]),
        datasets: chartDatasets,
      };

      return chartData;
    },

    getChartOptions() {
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          enabled: this.showTooltips,
        },
        legend: {
          display: this.showLegend,
          position: this.legendPosition,
        },
      };

      if (!this.scalesEnabled) {
        options.scales = {
          xAxes: [
            {
              display: false,
              barPercentage: this.barPercentage,
              categoryPercentage: this.categoryPercentage,
            },
          ],
          yAxes: [
            {
              display: false,
            },
          ],
        };
      }

      return options;
    },
  },
};
</script>



// WEBPACK FOOTER //
// AnalyticsChart.vue?0deb8368